import type { RootState } from '..';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { DocumentationPageType } from '../../components/documentation/types';
import type {
  BackendDocumentationMetadataTypesTab,
  DocumentationTabTypes,
} from '../../components/documentation/tabs/types';
import {
  loadMetadataTabs,
  saveMetadataTabs,
  getUserSelectedTabs,
  mergeWithDefaultTabs,
} from '../../lib/metadataTabsLocalStorage';

export const DEFAULT_OBJECTS_TAB_ID = 'objects';
const DEFAULT_METADATA_TABS = [
  { id: DEFAULT_OBJECTS_TAB_ID, label: 'Objects', pinned: true, _isNotRemovable: true },
];
const DEFAULT_TABS_FOR_TAGS_AND_USERS = [
  { id: 'tags', label: 'Tags', pinned: true, _isNotRemovable: true },
  { id: 'users', label: 'Users', pinned: true, _isNotRemovable: true },
];

export interface DocumentationPageState {
  documentationPageType: DocumentationPageType;
  pageTabs: { [key in DocumentationPageType]: DocumentationTabTypes[] };
  activePageTab?: string;
  metadataTypes: {
    [crmOrgId: string]: {
      types: BackendDocumentationMetadataTypesTab[];
      isLoading: boolean;
    };
  };
}

const initialState: DocumentationPageState = {
  documentationPageType: DocumentationPageType.metadataTypes,
  pageTabs: {
    [DocumentationPageType.metadataTypes]: DEFAULT_METADATA_TABS,
    [DocumentationPageType.tagsAndUsers]: DEFAULT_TABS_FOR_TAGS_AND_USERS,
    [DocumentationPageType.universalSearch]: [],
  },
  activePageTab: undefined,
  metadataTypes: {},
};

export const documentationPageSlice = createSlice({
  name: 'documentationPage',
  initialState,
  reducers: {
    setDocumentationPageType: (
      state,
      action: PayloadAction<{ documentationPageType: DocumentationPageType }>,
    ) => {
      state.documentationPageType = action.payload.documentationPageType;
    },
    loadTabsForOrg: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const { crmOrgId } = action.payload;
      const storedTabs = loadMetadataTabs(crmOrgId);

      if (storedTabs && storedTabs[DocumentationPageType.metadataTypes]) {
        const userTabs = getUserSelectedTabs(storedTabs[DocumentationPageType.metadataTypes]);
        const mergedTabs = mergeWithDefaultTabs(DEFAULT_METADATA_TABS, userTabs);
        state.pageTabs = {
          ...state.pageTabs,
          [DocumentationPageType.metadataTypes]: mergedTabs,
        };
      } else {
        // No stored data for this org, use defaults
        state.pageTabs = {
          ...state.pageTabs,
          [DocumentationPageType.metadataTypes]: DEFAULT_METADATA_TABS,
        };
      }
    },
    toggleTabVisibility: (
      state,
      action: PayloadAction<{ pageTypeId: string; isVisible: boolean; crmOrgId: string }>,
    ) => {
      const { pageTypeId, isVisible, crmOrgId } = action.payload;
      const relevantMetadataType = state.metadataTypes[crmOrgId]?.types.find(
        (type) => type.name === pageTypeId,
      );

      let tabsModified = false;

      if (relevantMetadataType && isVisible) {
        const alreadyExists = state.pageTabs[state.documentationPageType].find(
          (tab) => tab.id === pageTypeId,
        );
        if (alreadyExists) {
          state.pageTabs[state.documentationPageType] = state.pageTabs[
            state.documentationPageType
          ].map((tab) => (tab.id === pageTypeId ? { ...tab, pinned: isVisible } : tab));
          tabsModified = true;
        } else {
          state.pageTabs[state.documentationPageType].push({
            id: pageTypeId,
            label: pageTypeId, //TODO handle labels
            pinned: isVisible,
            _isNotRemovable: false,
          });
          state.activePageTab = pageTypeId;
          tabsModified = true;
        }
      } else if (relevantMetadataType && !isVisible) {
        state.pageTabs[state.documentationPageType] = state.pageTabs[
          state.documentationPageType
        ].filter((tab) => tab.id !== pageTypeId);
        if (state.activePageTab === pageTypeId) {
          state.activePageTab = DEFAULT_OBJECTS_TAB_ID;
        }
        tabsModified = true;
      }

      // Save to local storage only once if tabs were modified
      if (tabsModified) {
        const userTabs = getUserSelectedTabs(state.pageTabs[state.documentationPageType]);
        saveMetadataTabs(crmOrgId, { [DocumentationPageType.metadataTypes]: userTabs });
      }
    },
    setActivePageTab: (state, action: PayloadAction<{ tabId: string }>) => {
      state.activePageTab = action.payload.tabId;
    },
    clearActivePageTab: (state) => {
      state.activePageTab = undefined;
    },
    setMetadataTypes: (
      state,
      action: PayloadAction<{ types: BackendDocumentationMetadataTypesTab[]; crmOrgId: string }>,
    ) => {
      const { types, crmOrgId } = action.payload;
      state.metadataTypes[crmOrgId] = {
        types,
        isLoading: false,
      };

      // Check if we have local storage data
      const storedTabs = loadMetadataTabs(crmOrgId);

      // Only migrate server pinned tabs if local storage is empty
      if (!storedTabs || !storedTabs[DocumentationPageType.metadataTypes]) {
        const pinnedMetadataTypes = types
          .filter((type) => type.pinned)
          .map((type) => ({
            id: type.name,
            label: type.name,
            pinned: true,
            _isNotRemovable: false,
          }));

        if (pinnedMetadataTypes.length > 0) {
          state.pageTabs = {
            ...state.pageTabs,
            [DocumentationPageType.metadataTypes]: [
              ...DEFAULT_METADATA_TABS,
              ...pinnedMetadataTypes,
            ],
          };

          // Save the migrated tabs to local storage
          saveMetadataTabs(crmOrgId, {
            [DocumentationPageType.metadataTypes]: pinnedMetadataTypes,
          });
        }
      }
    },
    setMetadataTypesLoading: (
      state,
      action: PayloadAction<{ isLoading: boolean; crmOrgId: string }>,
    ) => {
      const { isLoading, crmOrgId } = action.payload;
      if (!state.metadataTypes[crmOrgId]) {
        state.metadataTypes[crmOrgId] = {
          types: [],
          isLoading,
        };
      } else {
        state.metadataTypes[crmOrgId].isLoading = isLoading;
      }
    },
  },
});

export const {
  toggleTabVisibility,
  setActivePageTab,
  setMetadataTypes,
  setMetadataTypesLoading,
  clearActivePageTab,
  setDocumentationPageType,
  loadTabsForOrg,
} = documentationPageSlice.actions;

export const selectPageTabs = (state: RootState) => state.documentationPage.pageTabs;
export const selectActiveDocumentationPageTab = (state: RootState) =>
  state.documentationPage.activePageTab;
export const selectMetadataTypes = (crmOrgId: string) => (state: RootState) =>
  state.documentationPage.metadataTypes[crmOrgId];
export const selectDocumentationPageType = (state: RootState) =>
  state.documentationPage.documentationPageType;

export default documentationPageSlice.reducer;
